import { useState, useEffect, useCallback } from 'react';
import { getDataset, updateDataset, deleteDataset } from '../services/datasetsApi';
import { listTestCases } from '../services/testCasesApi';
import { listDatasetItems } from '../services/datasetItemsApi';

/**
 * Custom hook for managing a single dataset
 * @param datasetId - Dataset ID to manage
 * @returns Dataset state and operations
 */
export const useDataset = (datasetId: string | null | undefined) => {
  const [dataset, setDataset] = useState<Record<string, any> | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);

  // Fetch dataset data
  const fetchDataset = useCallback(async (): Promise<void> => {
    if (!datasetId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await getDataset(datasetId);
      setDataset(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching dataset:', err);
    } finally {
      setLoading(false);
    }
  }, [datasetId]);

  // Load dataset on mount or when ID changes
  useEffect(() => {
    fetchDataset();
  }, [fetchDataset]);

  // Update dataset
  const update = useCallback(async (updates: Record<string, any>): Promise<any> => {
    if (!datasetId) return;

    setSaving(true);
    setError(null);

    try {
      const result = await updateDataset(datasetId, updates);
      setDataset(result);
      return result;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error updating dataset:', err);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [datasetId]);

  // Delete dataset
  const remove = useCallback(async (): Promise<void> => {
    if (!datasetId) return;

    try {
      await deleteDataset(datasetId);
      setDataset(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error deleting dataset:', err);
      throw err;
    }
  }, [datasetId]);

  // Refresh dataset data
  const refresh = fetchDataset;

  return {
    dataset,
    loading,
    error,
    saving,
    update,
    remove,
    refresh
  };
};

interface TestCasesOptions {
  searchTerm?: string;
  pageSize?: number;
}

/**
 * Custom hook for dataset test cases
 * @param datasetId - Dataset ID
 * @returns Test cases state and operations
 */
export const useDatasetTestCases = (datasetId: string | null | undefined, { searchTerm = '', pageSize = 50 }: TestCasesOptions = {}) => {
  const [testCases, setTestCases] = useState<Record<string, any>[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);

  const fetchTestCases = useCallback(async (params: Record<string, any> = {}): Promise<void> => {
    if (!datasetId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await listTestCases(datasetId, { page, limit: pageSize, searchTerm, ...params });
      setTestCases(response.data);
      setTotalPages(response.meta.totalPages);
      setTotal(response.meta.total);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching test cases:', err);
    } finally {
      setLoading(false);
    }
  }, [datasetId, page, pageSize, searchTerm]);

  useEffect(() => {
    fetchTestCases();
  }, [fetchTestCases]);

  return {
    testCases,
    loading,
    error,
    page,
    setPage,
    totalPages,
    total,
    refresh: fetchTestCases
  };
};

interface DatasetItemsOptions {
  itemType?: string | null;
  search?: string;
  pageSize?: number;
}

/**
 * Custom hook for dataset items (documents, qa_pairs, structured)
 * @param datasetId - Dataset ID
 * @param options - Optional filter by item type, search term, and page size
 * @returns Dataset items state and operations
 */
export const useDatasetItems = (datasetId: string | null | undefined, { itemType = null, search = '', pageSize = 50 }: DatasetItemsOptions = {}) => {
  const [items, setItems] = useState<Record<string, any>[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);

  const fetchItems = useCallback(async (params: Record<string, any> = {}): Promise<void> => {
    if (!datasetId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await listDatasetItems(datasetId, {
        page,
        limit: pageSize,
        itemType: itemType ?? undefined,
        search,
        ...params
      });
      setItems(response.data);
      setTotalPages(response.meta?.totalPages || 1);
      setTotal(response.meta?.total || 0);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching dataset items:', err);
    } finally {
      setLoading(false);
    }
  }, [datasetId, page, pageSize, itemType, search]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  return {
    items,
    loading,
    error,
    page,
    setPage,
    totalPages,
    total,
    refresh: fetchItems
  };
};

export default useDataset;
